import React from "react";
import { StatusPill } from "../../atoms/StatusPill";
import Spinner from "../../atoms/Spinner";
import { formatPipelineTime } from "../../../services/pipeline/pipelineMappers";
import { PillLabel, SpinnerSlot, Wrapper } from "./KgPipelineStatus.styles";
import type { KgPipelineStatusProps } from "./KgPipelineStatus.types";

const StatusIndicator: React.FC<KgPipelineStatusProps> = ({
  phase,
  errorMessage,
  completedAt,
  unavailableMessage,
}) => {
  if (unavailableMessage) {
    return (
      <StatusPill variant="warning" size="sm">
        <PillLabel title={unavailableMessage}>Status unavailable</PillLabel>
      </StatusPill>
    );
  }
  switch (phase) {
    case "polling":
      return (
        <StatusPill variant="info" size="sm" showDot={false}>
          <SpinnerSlot>
            <Spinner size={12} />
          </SpinnerSlot>
          Updating graph…
        </StatusPill>
      );
    case "completed": {
      const time = formatPipelineTime(completedAt);
      return (
        <StatusPill variant="success" size="sm">
          Graph updated{time ? ` · ${time}` : ""}
        </StatusPill>
      );
    }
    case "failed": {
      const message = errorMessage ?? "Unknown error";
      return (
        <StatusPill variant="error" size="sm">
          <PillLabel title={message}>Update failed: {message}</PillLabel>
        </StatusPill>
      );
    }
    default:
      return null;
  }
};

/**
 * Read-only status of the workspace's newest knowledge-graph build. The graph updates on
 * its own when its data changes, so there is nothing to trigger here.
 * Stateless: the organism reads the build via useKgLatestPipelineRun.
 */
export const KgPipelineStatus: React.FC<KgPipelineStatusProps> = (props) => (
  <Wrapper data-testid="kg-pipeline-status-wrapper">
    <span aria-live="polite" data-testid="kg-pipeline-status">
      <StatusIndicator {...props} />
    </span>
  </Wrapper>
);

export default KgPipelineStatus;
